import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "w-full px-2 sm:px-6 py-4" }
const _hoisted_2 = { class: "bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8" }
const _hoisted_3 = { class: "col-md-6" }
const _hoisted_4 = { class: "flex gap-3 items-start" }
const _hoisted_5 = { class: "flex-1" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { class: "bg-white rounded-xl shadow-sm border border-gray-200 p-8" }
const _hoisted_8 = { class: "flex items-center justify-between mb-6" }
const _hoisted_9 = { class: "text-sm text-black" }
const _hoisted_10 = {
  key: 0,
  class: "text-center py-12"
}
const _hoisted_11 = {
  key: 1,
  class: "overflow-x-auto"
}
const _hoisted_12 = { class: "min-w-full divide-y divide-gray-200" }
const _hoisted_13 = { class: "bg-white divide-y divide-gray-200" }
const _hoisted_14 = { class: "px-6 py-4 whitespace-nowrap" }
const _hoisted_15 = { class: "flex items-center" }
const _hoisted_16 = { class: "ml-4" }
const _hoisted_17 = { class: "text-sm font-medium text-black" }
const _hoisted_18 = { class: "text-sm text-black" }
const _hoisted_19 = { class: "px-6 py-4 whitespace-nowrap text-sm text-black" }
const _hoisted_20 = { class: "px-6 py-4 whitespace-nowrap text-sm font-medium" }
const _hoisted_21 = ["onClick"]
const _hoisted_22 = { class: "text-black" }
const _hoisted_23 = { class: "bg-gray-50 rounded-lg p-4 border border-gray-200" }
const _hoisted_24 = { class: "font-semibold text-black" }
const _hoisted_25 = { class: "text-sm text-black mt-1" }

import { ref, onMounted } from 'vue'
import { PageHeader, BuyerAutocompleteForBlocking, ModalComp } from '@/components'
import { getBlockedBuyers, blockBuyer, unblockBuyer } from '@/api'
import { useNotification } from '@/composables/useNotification'
import type { BlockedBuyer } from '@/types'
import {useRouter} from "vue-router";
import {useAuthStore} from "@/stores/auth";

// Notifications

export default /*@__PURE__*/_defineComponent({
  __name: 'BuyerManagementView',
  setup(__props) {

const { success: showSuccess, error: showError } = useNotification()

// État global
const blockedBuyers = ref<BlockedBuyer[]>([])
const selectedBuyerIds = ref<number[]>([])
const buyerAutocompleteRef = ref<{ clearSelection: () => void } | null>(null)

// Modal de confirmation
const showUnblockModal = ref(false)
const buyerToUnblock = ref<BlockedBuyer | null>(null)
const authStore = useAuthStore()
const router = useRouter()
// Méthodes
const loadBlockedBuyers = async () => {
  try {
    const data = await getBlockedBuyers()
    blockedBuyers.value = data
  } catch (error) {
    showError('Erreur lors du chargement des acheteurs bloqués')
  }
}

const handleBlockBuyer = async () => {
  if (!selectedBuyerIds.value.length) {
    showError('Veuillez sélectionner au moins un acheteur dans la liste')
    return
  }

  try {
    // Bloquer en série pour conserver les messages d'erreur potentiels par acheteur
    for (const id of selectedBuyerIds.value) {
      await blockBuyer(id)
    }

    // Réinitialiser le formulaire
    selectedBuyerIds.value = []
    buyerAutocompleteRef.value?.clearSelection()

    // Recharger la liste
    await loadBlockedBuyers()

    showSuccess('Acheteur(s) bloqué(s) avec succès')
  } catch (error) {
    showError('Erreur lors du blocage des acheteurs')
  }
}

const handleUnblockBuyer = (buyer: BlockedBuyer) => {
  buyerToUnblock.value = buyer
  showUnblockModal.value = true
}

const confirmUnblock = async () => {
  if (!buyerToUnblock.value) return
  
  try {
    await unblockBuyer(buyerToUnblock.value.id_vendeur_acheteur_disabled)
    await loadBlockedBuyers()
    showSuccess('Acheteur débloqué avec succès')
    buyerToUnblock.value = null
  } catch (error) {
    showError('Erreur lors du déblocage de l\'acheteur')
  }
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  await loadBlockedBuyers()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(PageHeader), {
      title: "Gestion des Acheteurs",
      subtitle: "Bloquez ou débloquez des acheteurs",
      class: "mb-0 pr-10"
    }),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _cache[4] || (_cache[4] = _createElementVNode("div", { class: "flex items-center justify-between mb-6" }, [
          _createElementVNode("h2", { class: "text-2xl font-bold text-black" }, "Bloquer un Acheteur")
        ], -1)),
        _createElementVNode("form", {
          onSubmit: _withModifiers(handleBlockBuyer, ["prevent"]),
          class: "space-y-6"
        }, [
          _createElementVNode("div", _hoisted_3, [
            _cache[3] || (_cache[3] = _createElementVNode("label", { class: "block text-sm font-medium text-black mb-2" }, " Nom de l'acheteur à bloquer ", -1)),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_unref(BuyerAutocompleteForBlocking), {
                  modelValue: selectedBuyerIds.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedBuyerIds).value = $event)),
                  multiple: true,
                  ref_key: "buyerAutocompleteRef",
                  ref: buyerAutocompleteRef
                }, null, 8, ["modelValue"])
              ]),
              _createElementVNode("button", {
                type: "submit",
                disabled: selectedBuyerIds.value.length === 0,
                class: "inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              }, _cache[2] || (_cache[2] = [
                _createElementVNode("i", { class: "fas fa-ban mr-2" }, null, -1),
                _createTextVNode(" Bloquer l'acheteur ")
              ]), 8, _hoisted_6)
            ])
          ])
        ], 32)
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          _cache[5] || (_cache[5] = _createElementVNode("h2", { class: "text-2xl font-bold text-black" }, "Acheteurs Bloqués", -1)),
          _createElementVNode("div", _hoisted_9, _toDisplayString(blockedBuyers.value.length) + " acheteur" + _toDisplayString(blockedBuyers.value.length > 1 ? 's' : '') + " bloqué" + _toDisplayString(blockedBuyers.value.length > 1 ? 's' : ''), 1)
        ]),
        (blockedBuyers.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, _cache[6] || (_cache[6] = [
              _createElementVNode("div", { class: "bg-green-50 border border-green-200 rounded-lg p-6 inline-block" }, [
                _createElementVNode("i", { class: "fas fa-check-circle text-green-400 text-4xl mb-4" }),
                _createElementVNode("div", { class: "text-green-800 font-medium mb-2" }, "Aucun acheteur bloqué"),
                _createElementVNode("div", { class: "text-green-600 text-sm" }, " Vous n'avez bloqué aucun acheteur pour le moment. ")
              ], -1)
            ])))
          : (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createElementVNode("table", _hoisted_12, [
                _cache[9] || (_cache[9] = _createElementVNode("thead", { class: "bg-gray-50" }, [
                  _createElementVNode("tr", null, [
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider" }, " Acheteur "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider" }, " Date de blocage "),
                    _createElementVNode("th", { class: "px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider" }, " Actions ")
                  ])
                ], -1)),
                _createElementVNode("tbody", _hoisted_13, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(blockedBuyers.value, (buyer) => {
                    return (_openBlock(), _createElementBlock("tr", {
                      key: buyer.id_vendeur_acheteur_disabled,
                      class: "hover:bg-gray-50"
                    }, [
                      _createElementVNode("td", _hoisted_14, [
                        _createElementVNode("div", _hoisted_15, [
                          _cache[7] || (_cache[7] = _createElementVNode("div", { class: "flex-shrink-0 h-10 w-10" }, [
                            _createElementVNode("div", { class: "h-10 w-10 rounded-full bg-red-100 flex items-center justify-center" }, [
                              _createElementVNode("i", { class: "fas fa-user text-red-600" })
                            ])
                          ], -1)),
                          _createElementVNode("div", _hoisted_16, [
                            _createElementVNode("div", _hoisted_17, _toDisplayString(buyer.acheteur?.nomSociete || 'N/A'), 1),
                            _createElementVNode("div", _hoisted_18, _toDisplayString(buyer.acheteur?.email || `ID: ${buyer.id_acheteur}`), 1)
                          ])
                        ])
                      ]),
                      _createElementVNode("td", _hoisted_19, _toDisplayString(formatDate(buyer.date_disabled)), 1),
                      _createElementVNode("td", _hoisted_20, [
                        _createElementVNode("button", {
                          onClick: ($event: any) => (handleUnblockBuyer(buyer)),
                          class: "inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                        }, _cache[8] || (_cache[8] = [
                          _createElementVNode("i", { class: "fas fa-unlock mr-1" }, null, -1),
                          _createTextVNode(" Débloquer ")
                        ]), 8, _hoisted_21)
                      ])
                    ]))
                  }), 128))
                ])
              ])
            ]))
      ])
    ]),
    _createVNode(_unref(ModalComp), {
      isVisible: showUnblockModal.value,
      "onUpdate:isVisible": _cache[1] || (_cache[1] = ($event: any) => ((showUnblockModal).value = $event)),
      title: "Confirmer le déblocage",
      size: "sm",
      cancelText: "Annuler",
      confirmText: "Débloquer",
      onConfirm: confirmUnblock
    }, {
      body: _withCtx(() => [
        _createElementVNode("div", _hoisted_22, [
          _cache[10] || (_cache[10] = _createElementVNode("p", { class: "mb-4" }, "Êtes-vous sûr de vouloir débloquer l'acheteur :", -1)),
          _createElementVNode("div", _hoisted_23, [
            _createElementVNode("div", _hoisted_24, _toDisplayString(buyerToUnblock.value?.acheteur?.nomSociete || 'N/A'), 1),
            _createElementVNode("div", _hoisted_25, _toDisplayString(buyerToUnblock.value?.acheteur?.email || ''), 1)
          ]),
          _cache[11] || (_cache[11] = _createElementVNode("p", { class: "mt-4 text-sm text-black" }, [
            _createElementVNode("i", { class: "fas fa-info-circle mr-1" }),
            _createTextVNode(" Cet acheteur pourra à nouveau commander chez vous. ")
          ], -1))
        ])
      ]),
      _: 1
    }, 8, ["isVisible"])
  ], 64))
}
}

})